import type React from 'react';
import { type JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ParseArticleContent, ParseHtmlDefault } from 'utils/miscUtils/rawHtml';
import { scrubContent } from 'utils/stringUtils';
import useDateTime from 'hooks/useDateTime';
import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import type { PresentedByProps } from 'components/PresentedBy';
import AdminBar from 'components/AdminBar';
import FeaturedHeroMedia from 'components/FeaturedHeroMedia';
import { UserPaywallContext } from 'providers/PaywallProvider';
import useBreakpoint from 'hooks/useBreakpoint';
import { Microsites, RedVentureWidgets, subdomainColors } from 'constants/constants';
import { UserContext } from 'providers/UserProvider';
import { color as colors } from 'styles/globals';
import type { StickyCTAProps } from 'components/StickyCTA';
import { getArticleTrackingData } from 'utils/trackDataUtils';
import useTrackArticleClickEvents from 'hooks/useTrackArticleClickEvents';
import * as RS from 'styles/rightRail';
import getArticlePageTestId, { ARTICLE_PAGE_TEST_IDS } from 'constants/testsIds/articlePage';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import { handleReadMoreToggle, toggleReadMoreBoxes } from 'utils/miscUtils/toggleReadMoreBoxes';
import ScrollUpButton from 'components/Globals/Base/ScrollUpButton';
import { PageType } from 'interfaces/content/articles/Post';
import useRedVentureIframeObserver from 'hooks/useRedVentureIframesObserver';
import { LANDING_PAGE_TEST_IDS } from 'constants/testsIds/landingPage';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import dynamic from 'next/dynamic';
import { SectionVariant } from 'interfaces/Section';
import ArticleSection from 'components/recommends/ArticleSection';
import IconSocialShareDropdown from 'components/IconSocialShareDropdown';
import AboutTheContributors from 'components/AboutTheContributors';
import * as S from './ArticleStyles';
import type { VariantProps } from 'providers/OptimizelyProvider';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));
const ArticleHeroEyebrow = dynamic(() => import('components/ArticleHeroEyebrow'));
const BylineList = dynamic(() => import('components/Byline'));
const ContributorsByline = dynamic(() => import('components/ContributorsByline'));
const PresentedBy = dynamic(() => import('components/PresentedBy'));

const ContentWithRVWidgets = dynamic(() => import('components/ContentWithRVWidgets'), {
  ssr: false,
});

const ContentWithEmbed = dynamic(() => import('components/ContentWithEmbed'), {
  ssr: false,
});

const ArticleSeriesTop = dynamic(() => import('components/ArticleSeries/ArticleSeriesTop'), {
  ssr: false,
});

const ArticleSeriesList = dynamic(() => import('components/ArticleSeries/ArticleSeriesList'), {
  ssr: false,
});

const StickyCTA = dynamic(() => import('components/StickyCTA'), {
  ssr: false,
});

export interface ArticleProps {
  article: Article;
  mostPopular?: ArticlePreview[];
  presentedBy?: PresentedByProps;
  sidebar?: (JSX.Element | null)[];
  footer?: JSX.Element[];
  adFrequency: number;
  embeds: { node: string; index: number; component: JSX.Element }[];
  lastPWithoutAds?: number;
  subdomain: string;
  stickyTop?: number | null;
  zipCode?: string;
  stickyCta?: StickyCTAProps;
  widgets?: { [key: string]: string };
  headerAds?: JSX.Element[];
  hasRedVentureWidgets?: boolean;
}

const ArticlePage: React.FC<ArticleProps> = ({
  article,
  mostPopular,
  presentedBy,
  sidebar,
  footer,
  adFrequency,
  embeds,
  lastPWithoutAds,
  subdomain,
  stickyTop = null,
  zipCode,
  widgets,
  headerAds,
  hasRedVentureWidgets,
}) => {
  const {
    authors,
    breadcrumbs,
    content,
    databaseId,
    dateGmt,
    modifiedGmt,
    showModifiedTimestamp,
    featuredMediaType,
    imageCaption,
    imageCredit,
    postSettings,
    videoId,
    title,
    reviewedBy,
    series,
    url,
    id,
    footnotes,
  } = article;

  const localDate = useDateTime(dateGmt);
  const updatedDate = useDateTime(modifiedGmt);
  const isUpdated = !(localDate === updatedDate);
  const cleanedContent = scrubContent(content);

  const keyTakeawaysWidget = widgets?.['key-takeaways-widget'];
  const stickyCtaWidget = widgets?.['sticky-cta-widget'];

  const widgetsInArticle = useMemo(
    () => Object.values(RedVentureWidgets).filter((widgetId) => content.indexOf(widgetId) > -1),
    [content],
  );

  const isRecommends = Microsites.Recommends === subdomain;
  const isWell = Microsites.Well === subdomain;
  const isEducation = Microsites.Education === subdomain;
  const isRecommendsTemplate = isRecommends || isEducation;

  const micrositeHasSeries = isRecommends || isEducation || isWell;

  const { headerHeight } = useHeaderHeight(isRecommendsTemplate);
  const { paywallCheck } = useContext(UserPaywallContext).paywallState;
  const { userState: user } = useContext(UserContext);

  const isMobile = useBreakpoint('mdMax');

  const RawHtmlClass = paywallCheck === 'no-paywall' && isMobile ? 'rawHtml-content' : 'rawHtml-content-no-nativo';

  const primarySection = {
    link: article.primarySection.uri,
    name: article.primarySection.name,
  };

  const primaryPostTag = {
    link: article.primaryPostTag.uri,
    name: article.primaryPostTag.name,
  };

  const { userFlags: userVariants } = useOptimizely();
  const shrinkSpacingVariants = userVariants.shrink_spacing_articles as VariantProps | undefined;
  const renderStickySpacing = shrinkSpacingVariants?.enabled ?? false;

  const [shrinkSpacingClass, setshrinkSpacingClass] = useState('');

  useEffect(() => {
    setshrinkSpacingClass(renderStickySpacing ? 'shrink-spacing' : '');
  }, [renderStickySpacing]);

  const breadcrumbsData = (
    <Breadcrumbs
      {...breadcrumbs}
      hasBrandColor
    />
  );

  const { trackingData } = useMemo(() => getArticleTrackingData(article, user, subdomain), [article, subdomain, user]);
  useTrackArticleClickEvents(trackingData, subdomain);

  useEffect(() => {
    if (isEducation && !isMobile) {
      const productCardAccordions = document.querySelectorAll('.card-accordion');
      if (productCardAccordions.length > 0) {
        productCardAccordions.forEach((accordion) => {
          const tabRadioButtons = accordion.querySelectorAll(
            'input[type="radio"]',
          ) as unknown as NodeListOf<HTMLInputElement>;
          const hasOneTabOpened = Array.from(tabRadioButtons).some((input) => input.checked);
          // ensure one tab is always opened on desktop
          if (tabRadioButtons.length > 0 && !hasOneTabOpened) {
            tabRadioButtons[0].checked = true;
          }
        });
      }
    }
  }, [isMobile, isEducation]);

  const handleReadMore = useCallback((event: MouseEvent) => {
    handleReadMoreToggle(event);
  }, []);

  useEffect(() => {
    toggleReadMoreBoxes(handleReadMore);
  }, [handleReadMore, adFrequency]);

  const domainColor = {
    [Microsites.Recommends]: colors.recommendsGreen,
    [Microsites.Well]: colors.wellBlue,
  };

  const contentRef = useRef<HTMLDivElement>(null);
  useRedVentureIframeObserver(contentRef, article.postId, hasRedVentureWidgets);

  return (
    // biome-ignore lint/a11y/useSemanticElements: not used anymore
    <S.ArticlePageWrapper
      className={shrinkSpacingClass}
      id='content'
      role='article'
      $isRecommends={isRecommends}
    >
      {presentedBy && (
        <PresentedBy
          {...presentedBy}
          className='presented-by-banner'
        />
      )}
      {isRecommendsTemplate && (
        <S.BreadcrumbsContainer className={shrinkSpacingClass}> {breadcrumbsData} </S.BreadcrumbsContainer>
      )}
      <ErrorBoundary fragment={UIFragments.ADMIN_BAR}>
        <AdminBar pageId={databaseId} />
      </ErrorBoundary>
      <S.ArticleHeroWrapper
        className={shrinkSpacingClass}
        data-cy={getArticlePageTestId('CONTAINER_EYEBROW')}
      >
        {!isRecommendsTemplate && (
          <ArticleHeroEyebrow
            primarySection={primarySection}
            primaryPostTag={primaryPostTag}
          />
        )}
        <S.ArticleHeroTitle
          className={`${shrinkSpacingClass} has-author-byline`}
          as='h1'
          $fontFamily='saol'
          $size={{ default: 'level3', mdMin: 'large' }}
          data-cy={getArticlePageTestId('TITLE')}
        >
          {title}
        </S.ArticleHeroTitle>
        <S.ArticleAuthorDateSocialsContainer className={shrinkSpacingClass}>
          <S.ArticleHeroBylineWrapper data-cy={getArticlePageTestId('AUTHOR_AND_DATE')}>
            <S.ArticleHeroAuthorsContainer>
              {isRecommendsTemplate ? (
                <ContributorsByline
                  authors={authors}
                  reviewedBy={reviewedBy}
                  trackingData={trackingData}
                  dataCy='authorByLine'
                />
              ) : (
                <BylineList
                  data-cy='articleAuthors'
                  bylineList={authors}
                  isArticle
                />
              )}
            </S.ArticleHeroAuthorsContainer>
            <S.ArticleDateSocialsContainer>
              {!(isRecommendsTemplate && showModifiedTimestamp && isUpdated) ? (
                <S.ArticleHeroDate data-cy={getArticlePageTestId('HEADER_DATE')}>{localDate}</S.ArticleHeroDate>
              ) : null}
              {showModifiedTimestamp && isUpdated ? <S.ArticleHeroDate>Updated {updatedDate}</S.ArticleHeroDate> : null}
              <IconSocialShareDropdown
                className='social-links'
                title={title}
              />
            </S.ArticleDateSocialsContainer>
          </S.ArticleHeroBylineWrapper>
        </S.ArticleAuthorDateSocialsContainer>
        {micrositeHasSeries &&
          series.map((seriesList, idx) => {
            const displaySeries = seriesList.name && seriesList?.posts?.length > 0;
            if (!displaySeries) return null;
            return (
              <ArticleSeriesTop
                seriesID={`articleSeries${idx}`}
                key={seriesList.name}
                series={seriesList}
                articleURL={url}
                className='articleSeriesTop'
                dataCy={ARTICLE_PAGE_TEST_IDS.SERIES_TOP}
              />
            );
          })}
      </S.ArticleHeroWrapper>
      <S.ArticleImageAndContentContainer className={shrinkSpacingClass}>
        <S.ArticleHeroImageContainer
          data-cy={getArticlePageTestId('IMAGE')}
          $isRecommends={isRecommendsTemplate}
        >
          <FeaturedHeroMedia
            className='article-video'
            featuredMediaType={featuredMediaType}
            imageCaption={imageCaption}
            imageCredit={imageCredit}
            postSettings={postSettings}
            videoId={videoId}
          />
        </S.ArticleHeroImageContainer>
        <S.ArticleContentWrapper
          className={shrinkSpacingClass}
          id='article-content'
          $subdomain={subdomain}
          $stickyTop={stickyTop}
          $subdomainColor={subdomainColors[subdomain]}
          $headerHeight={headerHeight}
        >
          {headerAds && headerAds.length > 0 && (
            <ErrorBoundary fragment={UIFragments.AD}>
              <S.HeaderAdsWrapper>{headerAds}</S.HeaderAdsWrapper>
            </ErrorBoundary>
          )}
          <ContentWithRVWidgets
            widgetIds={widgetsInArticle}
            microsite={subdomain}
            zipCode={zipCode}
            trackingData={trackingData}
            postId={article.postId}
          >
            {/** biome-ignore lint/complexity/noUselessFragments: ContentWithRVWidgets wants a singular child*/}
            <>
              <ErrorBoundary fragment={UIFragments.KEYTAKEAWAYS_WIDGET}>
                {keyTakeawaysWidget ? ParseHtmlDefault(keyTakeawaysWidget) : null}
              </ErrorBoundary>
              <ContentWithEmbed
                embedInstructions={embeds.map((embed) => ({ index: embed.index, node: embed.node }))}
                adFrequency={adFrequency}
                lastPWithoutAds={lastPWithoutAds}
                pageType={PageType.ARTICLE}
                section={primarySection.name}
                articleUrl={article.url}
              >
                {[
                  ...embeds.map((embed) => embed.component),
                  <div
                    key={`article-content-${adFrequency}`}
                    data-cy={getArticlePageTestId('CONTENT')}
                    className={RawHtmlClass}
                    ref={contentRef}
                  >
                    {ParseArticleContent(cleanedContent, true, footnotes)}
                  </div>,
                ]}
              </ContentWithEmbed>
            </>
          </ContentWithRVWidgets>
          {!article.hideAuthors && authors.length > 0 && (
            <AboutTheContributors
              authors={authors}
              className={shrinkSpacingClass}
              reviewedBy={reviewedBy}
              isEducation
            />
          )}
          {primarySection.name !== 'Commentary' && footer && <S.FooterStyles>{footer}</S.FooterStyles>}
          {micrositeHasSeries &&
            series.map((seriesList, idx) => {
              const displaySeries = seriesList.name && seriesList?.posts?.length > 0;
              if (!displaySeries) return null;
              return (
                <ArticleSeriesList
                  seriesID={`articleSeries${idx}`}
                  key={seriesList.name}
                  series={seriesList}
                  currentArticleId={id}
                  className='articleSeriesList'
                  color={domainColor[subdomain]}
                  dataCy={ARTICLE_PAGE_TEST_IDS.SERIES_LIST}
                />
              );
            })}
        </S.ArticleContentWrapper>
        {mostPopular && mostPopular.length > 0 && (
          <S.TopArticlesContainer $isEducation={isEducation}>
            <ArticleSection
              title='Top Articles'
              trackingData={trackingData}
              variant={SectionVariant.TopArticles2}
              articles={mostPopular}
            />
          </S.TopArticlesContainer>
        )}
      </S.ArticleImageAndContentContainer>
      <S.ArticleSidebarContainer>
        <ErrorBoundary fragment={UIFragments.ARTICLE_SIDEBAR}>
          <RS.RightRail data-cy={getArticlePageTestId('RIGHT_RAIL')}>{sidebar}</RS.RightRail>
        </ErrorBoundary>
      </S.ArticleSidebarContainer>
      <ScrollUpButton isRecommends />
      {!!stickyCtaWidget && (
        <StickyCTA dataCy={LANDING_PAGE_TEST_IDS.STICKY_CTA}>{ParseHtmlDefault(stickyCtaWidget)}</StickyCTA>
      )}
    </S.ArticlePageWrapper>
  );
};

export { ArticlePage };
